import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Button, Table, message, Typography, Space, Alert, Tag, Input as AntInput } from 'antd';
import axios from 'axios';
import { API_URL } from '../config';

const { Text, Paragraph } = Typography;
const { Search } = AntInput;

interface CreateFloorModalProps {
  visible: boolean;
  hotelId: number;
  onClose: () => void;
  onSuccess: () => void;
}

interface RoomRow {
  id: number;
  name: string;
  floorNumber: string | null;
  categoryName?: string;
}

const CreateFloorModal: React.FC<CreateFloorModalProps> = ({
  visible,
  hotelId,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [rooms, setRooms] = useState<RoomRow[]>([]);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [selectedRoomIds, setSelectedRoomIds] = useState<number[]>([]);
  const [roomSearch, setRoomSearch] = useState('');

  useEffect(() => {
    if (visible && hotelId !== undefined) {
      loadRooms();
    } else if (!visible) {
      form.resetFields();
      setSelectedRoomIds([]);
      setRoomSearch('');
      setRooms([]);
    }
  }, [visible, hotelId]);

  const loadRooms = async () => {
    setLoadingRooms(true);
    try {
      const response = await axios.get(`${API_URL}/hotels/${hotelId}/rooms`);
      const data: any[] = response.data;

      const uniqueMap = new Map<number, RoomRow>();
      for (const item of data) {
        const id = item.id;
        if (!id || uniqueMap.has(id)) continue;
        uniqueMap.set(id, {
          id,
          name: item.room || `Room ${id}`,
          floorNumber: item.floor || null,
          categoryName: item.roomType && item.roomType !== '-' ? item.roomType : undefined,
        });
      }
      setRooms(
        Array.from(uniqueMap.values()).sort((a, b) => {
          const aNum = parseInt(a.name, 10);
          const bNum = parseInt(b.name, 10);
          if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum;
          return a.name.localeCompare(b.name);
        }),
      );
    } catch (error) {
      console.error('Failed to load rooms:', error);
      message.error('Failed to load rooms');
    } finally {
      setLoadingRooms(false);
    }
  };

  const handleSubmit = async (values: any) => {
    const floorNumber = (values.floorNumber || '').trim();
    if (!floorNumber) {
      message.warning('Please enter a floor number');
      return;
    }

    setSubmitting(true);
    try {
      const names: Record<string, string> = {};
      if (values.nameEn && values.nameEn.trim()) {
        names['en-US'] = values.nameEn.trim();
      }

      await axios.post(`${API_URL}/floors`, {
        hotelId,
        floorNumber,
        names: Object.keys(names).length > 0 ? names : undefined,
      });

      if (selectedRoomIds.length > 0) {
        const assignResp = await axios.post(`${API_URL}/rooms/bulk-assign-floor`, {
          hotelId,
          floorNumber,
          roomIds: selectedRoomIds,
        });
        const { updated, notFound } = assignResp.data;
        message.success(
          `Floor "${floorNumber}" created. Assigned ${updated} room(s)` +
            (notFound > 0 ? `, ${notFound} not found` : ''),
        );
      } else {
        message.success(`Floor "${floorNumber}" created`);
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      const msg = error.response?.data?.message || error.message || 'Failed to create floor';
      message.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const filteredRooms = roomSearch.trim()
    ? rooms.filter((r) =>
        r.name.toLowerCase().includes(roomSearch.toLowerCase()) ||
        (r.floorNumber || '').toLowerCase().includes(roomSearch.toLowerCase()),
      )
    : rooms;

  const columns = [
    {
      title: 'Room',
      dataIndex: 'name',
      key: 'name',
      width: 140,
    },
    {
      title: 'Current Floor',
      dataIndex: 'floorNumber',
      key: 'floorNumber',
      render: (val: string | null) =>
        val ? <Tag color="blue">{val}</Tag> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Room Type',
      dataIndex: 'categoryName',
      key: 'categoryName',
      render: (val?: string) => val || <Text type="secondary">—</Text>,
    },
  ];

  return (
    <Modal
      title="Create Floor"
      open={visible}
      onCancel={onClose}
      width={800}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={submitting}
          onClick={() => form.submit()}
        >
          Create Floor
          {selectedRoomIds.length > 0 ? ` & Assign ${selectedRoomIds.length} Room(s)` : ''}
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          label="Floor Number"
          name="floorNumber"
          rules={[{ required: true, message: 'Please enter a floor number' }]}
          extra="The floor identifier (e.g. '1', '2', 'Ground'). Must be unique within this hotel."
        >
          <Input placeholder="e.g. 1" />
        </Form.Item>

        <Form.Item
          label="Floor Name (English, optional)"
          name="nameEn"
          extra="Optional display name. You can add names in other languages later from the floor's edit view."
        >
          <Input placeholder="e.g. First Floor" />
        </Form.Item>

        <Paragraph strong style={{ marginTop: 12, marginBottom: 8 }}>
          Assign Rooms to this Floor (optional)
        </Paragraph>

        <Space direction="vertical" size="small" style={{ width: '100%', marginBottom: 8 }}>
          {selectedRoomIds.length > 0 && (
            <Alert
              type="info"
              showIcon
              message={`${selectedRoomIds.length} room(s) selected`}
              description="Rooms that already have a floor will be reassigned to the new floor."
            />
          )}
          <Search
            placeholder="Search rooms..."
            allowClear
            value={roomSearch}
            onChange={(e) => setRoomSearch(e.target.value)}
            style={{ width: 300 }}
          />
        </Space>

        <Table
          rowKey="id"
          size="small"
          loading={loadingRooms}
          dataSource={filteredRooms}
          columns={columns}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          scroll={{ y: 300 }}
          rowSelection={{
            selectedRowKeys: selectedRoomIds,
            onChange: (keys) => setSelectedRoomIds(keys as number[]),
            preserveSelectedRowKeys: true,
          }}
        />
      </Form>
    </Modal>
  );
};

export default CreateFloorModal;
